import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Modal,
  ModalContent,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import {
  useAppointment,
  useCreateTransaction,
  useMarkSampleCollected,
} from "api/appointments";
import PageTitle from "generics/PageTitle";
import { Tabs } from "generics/Tabs";
import ToolbarPortal from "generics/ToolbarPortal";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import PatientDetails from "./PatientDetails";
import { Controller, useForm } from "react-hook-form";

export type ObjectValues<T> = T[keyof T];

export const PAYMENT_SETTLEMENT = {
  PAID_TO_CURA: "paid_to_cura",
  PAID_TO_PARTNERED_LAB: "paid_to_partnered_lab",
};
export type PaymentSettlement = ObjectValues<typeof PAYMENT_SETTLEMENT>;


export default function AppointmentDetailPage() {
  const { id } = useParams();
  const appointmentQuery = useAppointment(id);

  const [createTransaction, createTransactionInfo] = useCreateTransaction();
  const [markSampleCollected, markSampleCollectedInfo] =
    useMarkSampleCollected();

  const appointment = appointmentQuery.data;
  const [selectedPatient, setSelectedPatient] = useState<string | undefined>();
  const paymentSettlementSelectionModal = useDisclosure()

  const { handleSubmit, register, control, errors, watch } = useForm({});

  function coloredPaymentStatus(status: String) {
    return (
      <Text
        fontWeight="500"
        color={status === "paid" ? "teal.500" : "orange.500"}
      >
        {status.toUpperCase()}
      </Text>
    );
  }

  if (appointmentQuery.isLoading)
    return (
      <Box>
        <Flex alignItems="center" justifyContent="center" h="200px">
          <Spinner color="teal.500" size="xl" />
        </Flex>
      </Box>
    );

  const openSelectSettlementSelectionModal = () => {
    paymentSettlementSelectionModal.onOpen()
  }

  const onSubmit = async (values: any) => {
    const { paymentSettlement } = values
    paymentSettlementSelectionModal.onOpen()
    await createTransaction({ ...appointment, paymentSettlement })
    paymentSettlementSelectionModal.onClose()

  }

  if (!appointment) return <h1>No Appointments</h1>;

  function renderActionButton() {
    if (appointment.status === "sample_collected") return;
    if (appointment.status === "active") {
      if (
        !appointment.paymentStatus ||
        appointment.paymentStatus === "pending"
      ) {
        return (
          <Button
            onClick={() => {
              openSelectSettlementSelectionModal()
            }
            }
            isLoading={createTransactionInfo.isLoading}
            disabled={createTransactionInfo.isLoading}
          >
            Mark as Paid and Sample Collected
          </Button>
        );
      }
      return (
        <Button
          onClick={() => markSampleCollected(appointment)}
          isLoading={markSampleCollectedInfo.isLoading}
          disabled={markSampleCollectedInfo.isLoading}
        >
          Mark as Sample Collected
        </Button>
      );
    }
  }
  const watchPayment = watch('paymentSettlement')


  return (
    <Box>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <PageTitle flexGrow={1}>
            Appointment: User #{appointment.user?.readableId}
          </PageTitle>
        </Flex>
      </ToolbarPortal>
      <Box px={12} py={6}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="4xl" fontWeight="500" color="black.500">
            {appointment.user?.name}
          </Text>
          <Badge colorScheme="green" py={2}>
            {appointment.status.split("_").join(" ")}
          </Badge>
        </Flex>
        <Box>
          <Stack direction="row" spacing="3" align="center">
            <Text fontWeight="medium">Rs. {appointment.fee}</Text>
            {coloredPaymentStatus(appointment.paymentStatus)}
          </Stack>
        </Box>

        <Modal isOpen={paymentSettlementSelectionModal.isOpen} size="sm" onClose={paymentSettlementSelectionModal.onClose}>
          <ModalOverlay />
          <ModalContent
            boxShadow="none"
            height="80vh"
            bg="white"
          >
            <Box padding={5} >
              <Heading>Payment Settlement</Heading>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormControl mt={10} isInvalid={errors.status}>
                  <FormLabel htmlFor="paymentSettlement">Select payment made to</FormLabel>
                  <Controller
                    as={
                      <Select
                        disabled={false}
                        placeholder="Select settlement option"
                        ref={register}
                      >
                        <option value={PAYMENT_SETTLEMENT.PAID_TO_CURA}>Paid to Cura</option>
                        <option value={PAYMENT_SETTLEMENT.PAID_TO_PARTNERED_LAB}>Paid to Partnered Lab</option>
                      </Select>
                    }
                    name="paymentSettlement"
                    control={control}
                    rules={{ required: true }}
                    defaultValue=""
                  />
                  <FormErrorMessage>Please select payment made to</FormErrorMessage>
                </FormControl>
                <Button
                  mt="20px"
                  disabled={!watchPayment}
                  isLoading={createTransactionInfo.isLoading}
                  type="submit"
                >
                  Submit
                </Button>
              </form>

            </Box>
          </ModalContent>
        </Modal>

        <Box py="4">{renderActionButton()}</Box>
        <Divider />
        <Tabs
          onChange={(tab) => setSelectedPatient(tab)}
          tabs={Object.values(appointment.patients).map((patient: any) => ({
            name: patient.patientName,
            value: patient.profileId,
          }))}
        />
        <Box pt={4}>
          <PatientDetails
            key={Date.now()}
            appointment={appointment}
            patient={
              selectedPatient
                ? appointment.patients[selectedPatient]
                : Object.values(appointment.patients)[0]
            }
          />
        </Box>
      </Box>
    </Box >
  );
}
